/**
 * Dashboard — top-level layout for the new Home page. Reads the bootstrap
 * payload (widget catalog + saved layout) and renders one card per
 * visible widget on a 4-column responsive grid that matches the Penpot
 * mockup (Top Group + Chart Row, but expressed as a single grid so
 * widgets flow naturally on mobile).
 *
 * Header pairs a time-of-day greeting ("Good morning, Juan") on the left
 * with the global date-range dropdown on the right. The DateRangeProvider
 * wraps the grid so every widget hook reads the same range key and
 * refetches when it changes.
 */

import * as React from "react";
import { useAuthContext } from "contexts/auth";
import { DateRangeSelect } from "components/dashboard";
import { useBootstrap } from "./useBootstrap";
import { resolveWidget } from "./widgets";
import {
  DASHBOARD_RANGE_KEYS,
  DASHBOARD_RANGE_LABELS,
  DashboardRangeProvider,
  useDashboardRangeControl,
} from "./RangeContext";
import type { DashboardRangeKey } from "./RangeContext";
import type { LayoutItem, WidgetConfig, WidgetKey } from "./types";
import "./Dashboard.css";

const RANGE_OPTIONS = DASHBOARD_RANGE_KEYS.map((key) => ({
  label: DASHBOARD_RANGE_LABELS[key],
  value: key,
}));

export default function Dashboard() {
  return (
    <DashboardRangeProvider>
      <DashboardContent />
    </DashboardRangeProvider>
  );
}

function DashboardContent() {
  const { data, isLoading, error } = useBootstrap();
  const auth = useAuthContext() as { name?: string } | null;
  const firstName = ((auth?.name ?? "").split(" ")[0] || "").trim();

  if (isLoading) {
    return <DashboardSkeleton firstName={firstName} />;
  }

  if (error || !data) {
    return (
      <div className="p-6 text-sm text-red-600">
        Failed to load dashboard:{" "}
        {error instanceof Error ? error.message : "unknown error"}
      </div>
    );
  }

  const layout = data.userLayout.length > 0 ? data.userLayout : data.defaultLayout;
  const catalog = new Map<WidgetKey, WidgetConfig>(
    data.widgetCatalog.map((w) => [w.key, w]),
  );
  const visible: Array<{ widget: WidgetConfig; item: LayoutItem }> = layout
    .filter((item) => !item.hidden)
    .map((item) => {
      const widget = catalog.get(item.key);
      return widget ? { widget, item } : null;
    })
    .filter((x): x is { widget: WidgetConfig; item: LayoutItem } => x !== null);

  return (
    <div className="p-4">
      <DashboardHeader firstName={firstName} />
      {/*
        auto-rows-min on mobile lets cards size to content (page scrolls).
        At lg+, fixed 160px row units keep the grid consistent — internally,
        widget bodies scroll via WidgetCard's overflow-y-auto.
        Total Top Group = 2 rows × 160 + gap = 332px; Sidebar (h=4) = 676px;
        Chart Row (h=1) = 160px.
      */}
      <div className="dashboard-grid grid grid-cols-1 sm:grid-cols-4 gap-3 auto-rows-min lg:auto-rows-[160px]">
        {visible.map(({ widget, item }) => {
          const Component = resolveWidget(widget.key);
          return <Component key={widget.key} widget={widget} layout={item} />;
        })}
      </div>
    </div>
  );
}

function DashboardHeader({ firstName }: { firstName: string }) {
  const { rangeKey, setRangeKey } = useDashboardRangeControl();
  const greeting = greetingForHour(new Date().getHours());
  const subject = firstName ? `${greeting}, ${firstName}` : greeting;

  return (
    <header className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
      <h1 className="text-xl font-semibold text-gray-800 m-0">{subject}</h1>
      <DateRangeSelect<DashboardRangeKey>
        options={RANGE_OPTIONS}
        value={rangeKey}
        onChange={setRangeKey}
        ariaLabel="Dashboard date range"
      />
    </header>
  );
}

/**
 * Greeting by local hour:
 *   00:00 – 11:59 → "Good morning"
 *   12:00 – 16:59 → "Good afternoon"
 *   17:00 – 23:59 → "Good evening"
 */
function greetingForHour(hour: number): string {
  if (hour < 12) return "Good morning";
  if (hour < 17) return "Good afternoon";
  return "Good evening";
}

function DashboardSkeleton({ firstName }: { firstName: string }) {
  const greeting = greetingForHour(new Date().getHours());
  const subject = firstName ? `${greeting}, ${firstName}` : greeting;
  return (
    <div className="p-4">
      <header className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <h1 className="text-xl font-semibold text-gray-800 m-0">{subject}</h1>
        <div className="h-9 w-[140px] bg-gray-100 rounded animate-pulse" />
      </header>
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
        {Array.from({ length: 9 }).map((_, i) => (
          <div key={i} className="bg-white rounded-md shadow-sm p-4 min-h-[140px] animate-pulse">
            <div className="h-3 w-1/2 bg-gray-200 rounded mb-3" />
            <div className="space-y-2">
              <div className="h-3 w-3/4 bg-gray-100 rounded" />
              <div className="h-3 w-2/3 bg-gray-100 rounded" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
